import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { VideoIcon } from '~/training/sequence/templates/items/video-popup/components/VideoIcon';
import { TickMarkIcon } from '~/training/sequence/templates/items/audio-popup/components/TickMarkIcon';
import { HotspotPlusIcon } from '~/training/sequence/templates/items/audio-popup/components/HotspotPlusIcon';
import { HotspotNumberLabel } from '~/training/sequence/templates/items/audio-popup/components/HotspotNumberLabel';
import { usePopup } from '~/training/sequence/templates/items/audio-popup/components/usePopup';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import { useCanControl } from '~/ui/video-player/useCanControl';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { drillAnimation } from '~/ui/animation';
import { HotspotFocusScope } from '~/lib/a11y/HotspotFocusScope';
import { t } from 'ttag';
import { MediaSource } from '~/ui/video-player/mediaSource';
import { useMediaBlocker } from '~/training/sequence/useMediaBlocker';

const selector = (s: MediaPlayerStore) => select(s, 'status', 'play', 'pause');

const seqSelector = (s: SequenceStore) =>
  select(s, 'setAudioPlayed', 'addBlocker', 'removeBlocker');

const DrillBoxAnimation = styled.div`
  width: 85px;
  height: 85px;
  animation: ${drillAnimation} 2.3s ease infinite;
  position: absolute;
  background-color: accentDark;
  border-radius: 50%;
  opacity: 0.5;
  z-index: 1;
`;

export const PopupButton = ({
  mediaUrl,
  mediaSources,
  id,
  blocking,
  title,
  replay,
  children
}: {
  mediaUrl?: string;
  mediaSources?: MediaSource;
  id: string;
  blocking: boolean;
  subtitlesUrl?: string;
  snapshotUrl?: string;
  title?: number;
  replay?: boolean;
  children?: React.ReactNode;
}) => {
  const { status, play, pause } = useMediaPlayerStore(selector, shallow);
  const { open, setOpen, popupRef } = usePopup();
  const visited = useRef(false);

  const ref = useRef<HTMLDivElement>();
  const { addBlocker, removeBlocker, setAudioPlayed } = useSequenceStore(
    seqSelector,
    shallow
  );

  useGlobalMedia();

  useEffect(() => pause(), []);

  const source = mediaSources ?? mediaUrl;
  const hasMedia = Array.isArray(source) ? source.length > 0 : Boolean(source);
  useMediaBlocker(id, blocking && hasMedia, source, 'video');

  useEffect(() => {
    if (!blocking || hasMedia) return;
    if (open) visited.current = true;
    if (visited.current) removeBlocker(id);
    else addBlocker(id);
    return () => removeBlocker(id);
  }, [blocking, hasMedia, id, open, addBlocker, removeBlocker]);

  useEffect(() => {
    if (!open) return;
    function updatePosition() {
      const rect = ref?.current?.getBoundingClientRect();
      const popupRect = popupRef?.current?.getBoundingClientRect();
      if (!rect || !popupRect) return;
    }
    window.addEventListener('resize', updatePosition);
    updatePosition();
    return () => window.removeEventListener('resize', updatePosition);
  }, [ref.current, popupRef.current, open]);

  useEffect(() => {
    if (status != 'done' && !open) {
      pause();
    }
  }, [status, open]);

  useEffect(() => {
    if (status === 'done') {
      setOpen(false);
      setAudioPlayed(id);
    }
  }, [status]);

  // Hide the button when another media player is playing
  const canControl = useCanControl() || status != 'playing';

  const clickHandler = () => {
    if (status === 'done' && !replay) return;

    play();
    setOpen(true);
  };

  const getBtnIcon = () => {
    if (title != null) {
      return <HotspotNumberLabel $fontSize={24}>{title}</HotspotNumberLabel>;
    }
    return <HotspotPlusIcon aria-hidden />;
  };

  return (
    <HotspotFocusScope
      w="85px"
      h="85px"
      backgroundColor={
        canControl && status === 'done'
          ? 'rgba(255,255,255,0.2)'
          : 'transparent'
      }
      borderRadius="50%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      position="relative"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
    >
      {canControl && (status !== 'done' || replay) && (
        <DrillBoxAnimation className="drillBoxEffect" />
      )}
      {open &&
        status == 'playing' &&
        ReactDOM.createPortal(
          <>
            <MediaCaptions />
          </>,
          document.getElementById('root')!
        )}
      {canControl && (
        <x.button
          onClick={clickHandler}
          backgroundColor="white"
          borderRadius="50%"
          w="67px"
          h="67px"
          display="flex"
          justifyContent="center"
          alignItems="center"
          overflow="hidden"
          zIndex={2}
          aria-label={t`play video`}
        >
          {mediaUrl ? (
            status === 'done' ? (
              <TickMarkIcon />
            ) : (
              <VideoIcon />
            )
          ) : (
            getBtnIcon()
          )}
        </x.button>
      )}

      {open &&
        ReactDOM.createPortal(
          <div ref={popupRef} onClick={e => e.stopPropagation()}>
            {children}
          </div>,
          document.getElementById('root')!
        )}
    </HotspotFocusScope>
  );
};
